import React, { useState } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Input from '~/components/molecule/Input';
import hasValue from '~/util/hasValue';

const text = {
  estimatedLeadscoreLabel: 'Verhoging leadscore',
  aiLeadscoreLabel: 'AI leadscore',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  aiScore?: { enabled: boolean; score: number };
  score: {
    value: number;
    onChange: (value: number | null) => void;
  };
};

/**
 * Parses and validates the score.
 */
const validateScore = (
  value: string,
): { isValid: true; value: number } | { isValid: false; error: string } => {
  if (!/^-?\d+$/.test(value)) {
    return { isValid: false, error: 'Min -1000, max 1000' };
  }

  const numValue = parseInt(value, 10);

  if (isNaN(numValue)) return { isValid: false, error: 'Min -1000, max 1000' };
  if (numValue > 1000) return { isValid: false, error: 'Max 1000' };
  if (numValue < -1000) return { isValid: false, error: 'Min -1000' };

  return { isValid: true, value: numValue };
};

const LeadscoreInput: React.FCC<Props> = ({
  dataTestId,
  className,
  // Not yet implemented, will be false until it is not
  aiScore = { enabled: false, score: 0 },
  score,
}) => {
  const [typedValue, setTypedValue] = useState<string>(String(score.value));

  return (
    <JustificationContainer
      width="100%"
      gap="xs"
      dataTestId={dataTestId}
      className={className}
    >
      <Input
        type="text"
        inputMode="numeric"
        size="small"
        width="100%"
        dataTestId="leadscore-input"
        label={{ text: text.estimatedLeadscoreLabel }}
        placeholder="0"
        value={typedValue}
        validation={[
          (value: string) => {
            const validation = validateScore(value);
            if (validation.isValid) return true;

            return validation.error;
          },
        ]}
        onChange={event => {
          const value = event.target.value;
          setTypedValue(value);

          // Convert to a number and check range
          const validation = validateScore(value);
          if (validation.isValid) score.onChange(validation.value);
        }}
        onBlur={() => {
          if (!hasValue(score.value)) {
            score.onChange(0);
          }
        }}
      />

      {aiScore?.enabled && (
        <Input
          disabled
          type="number"
          size="small"
          dataTestId="ai-score-input"
          label={{ text: text.aiLeadscoreLabel }}
          width="100%"
          value={aiScore.score ?? 0}
        />
      )}
    </JustificationContainer>
  );
};

export default LeadscoreInput;
